import type {
  Avatar,
  PlatformPreset,
  StyleOption,
  Voice,
} from "./types";

export const PLATFORM_PRESETS: PlatformPreset[] = [
  { id: "tiktok", label: "TikTok", ratio: "9:16", duration: [15, 45] },
  { id: "reels", label: "Instagram Reels", ratio: "9:16", duration: [15, 60] },
  { id: "shorts", label: "YouTube Shorts", ratio: "9:16", duration: [15, 60] },
  { id: "amazon", label: "Amazon Listing", ratio: "1:1", duration: [20, 60] },
  { id: "shopee", label: "Shopee", ratio: "1:1", duration: [10, 60] },
  { id: "lazada", label: "Lazada", ratio: "1:1", duration: [15, 60] },
  { id: "taobao", label: "Taobao / Tmall", ratio: "4:5", duration: [15, 60] },
];

export const STYLE_OPTIONS: StyleOption[] = [
  {
    id: "ugc",
    label: "UGC Testimonial",
    description: "Handheld, authentic creator vibe — highest converting",
    tone: "casual, sincere, first-person, like a friend's recommendation",
  },
  {
    id: "cinematic",
    label: "Cinematic Brand",
    description: "Polished camera moves, great for premium products",
    tone: "restrained, visual, short punchy sentences about craft and material",
  },
  {
    id: "unboxing",
    label: "Unboxing",
    description: "Unseal → details → hands-on, high info density",
    tone: "talk while unboxing, first impressions with a sense of surprise",
  },
  {
    id: "review",
    label: "Comparison Review",
    description: "Pain-point driven, great for functional products",
    tone: "rational, comparative, pain point first then the fix, data-driven",
  },
  {
    id: "promo",
    label: "Hard Promo",
    description: "Price and offer up front, built for sale events",
    tone: "urgent and punchy, repeat the price and the deal",
  },
  {
    id: "motion",
    label: "Product Motion Ad",
    description:
      "No presenter — your product photos become moving shots with camera moves and lighting",
    tone: "minimal voiceover, present tense, describes what the camera shows rather than speaking to camera",
  },
  {
    id: "faceless",
    label: "Faceless Video",
    description:
      "No presenter — product and supporting visuals are cut to AI voiceover, captions, and music",
    tone: "clear narrator-led storytelling, strong opening hook, concise scene-by-scene voiceover, no on-camera speaker",
  },
  {
    id: "ai_creator",
    label: "AI Creator UGC",
    description:
      "Played by one of our studio avatars, styled as native creator content — POV or day-in-the-life, product woven in naturally",
    tone: "creator voice, trend-aware pacing, product mentioned mid-scene like a sponsored post rather than pitched upfront",
  },
  {
    id: "clone_creator",
    label: "Real/Clone Creator UGC",
    description:
      "Same creator-style format, played by your own cloned avatar instead of a studio preset — upload photos/video on the Avatars page first",
    tone: "creator voice, trend-aware pacing, product mentioned mid-scene like a sponsored post rather than pitched upfront",
  },
  {
    id: "comment_ad",
    label: "Product Comment Ad",
    description:
      "No presenter — product visuals surrounded by floating social-proof comments and reviews",
    tone: "text-overlay driven, comment snippets do the talking, minimal or no voiceover, casual caption language",
  },
  {
    id: "commentary",
    label: "Talking Head Commentary Ad",
    description:
      "Presenter talks straight to camera giving their honest take — more opinionated vlogger energy than a scripted pitch",
    tone: "conversational monologue, opinionated first-person commentary, reacts to the product like a review vlog rather than reading a script",
  },
  {
    id: "pov_unboxing",
    label: "POV Product Demo/Unboxing",
    description:
      "No face on camera — first-person hands-only view opening the box or demoing the product, native to how TikTok's most-shared organic content looks",
    tone: "hands-only POV framing, casual real-time narration as if filming yourself, minimal scripted pitch",
  },
  {
    id: "presenter_ad",
    label: "On-Camera Presenter Ad",
    description:
      "A studio avatar holds and presents your product directly to camera — polished sales-host delivery for a clear, structured pitch",
    tone: "confident and professional, structured intro–benefits–CTA delivery, sells the product directly like a dedicated sales host",
  },
  {
    id: "spokesperson",
    label: "Spokesperson",
    description:
      "A studio avatar speaks as an official brand representative — formal statement-style delivery for announcements and brand trust rather than a direct sales pitch",
    tone: "formal, measured, speaks on behalf of the brand rather than pitching directly, builds credibility and trust over urgency",
  },
];

/** thumbnail 存的是 CSS gradient，前端直接当背景用，避免依赖外部图床 */
export const AVATARS: Avatar[] = [
  {
    id: "av_mia",
    name: "Mia",
    gender: "female",
    ageRange: "22-28",
    locale: "en-US",
    styles: ["ugc", "unboxing", "promo"],
    thumbnail: "linear-gradient(135deg,#f5b8d0,#c4a5f7)",
    premium: false,
  },
  {
    id: "av_jake",
    name: "Jake",
    gender: "male",
    ageRange: "28-35",
    locale: "en-US",
    styles: ["review", "cinematic"],
    thumbnail: "linear-gradient(135deg,#8fd3f4,#5b7cfa)",
    premium: false,
  },
  {
    id: "av_ada",
    name: "Ada",
    gender: "female",
    ageRange: "25-32",
    locale: "en-GB",
    styles: ["ugc", "review", "promo"],
    thumbnail: "linear-gradient(135deg,#ffd8a8,#ff8787)",
    premium: false,
  },
  {
    id: "av_marcus",
    name: "Marcus",
    gender: "male",
    ageRange: "30-40",
    locale: "en-US",
    styles: ["cinematic", "review"],
    thumbnail: "linear-gradient(135deg,#a0e7c4,#3bb2a0)",
    premium: true,
  },
  {
    id: "av_yuki",
    name: "Chloe",
    gender: "female",
    ageRange: "20-26",
    locale: "en-AU",
    styles: ["ugc", "unboxing"],
    thumbnail: "linear-gradient(135deg,#c3f0ff,#9aa7ff)",
    premium: true,
  },
  {
    id: "av_lin",
    name: "Nina",
    gender: "female",
    ageRange: "24-30",
    locale: "en-US",
    styles: ["ugc", "promo"],
    thumbnail: "linear-gradient(135deg,#ffe7a3,#f5a35c)",
    premium: false,
  },
];

const LEGACY_PROVIDER_VOICES: Voice[] = [
  { id: "vo_emma", name: "Emma — Warm", locale: "en-US", gender: "female", speed: 1 },
  { id: "vo_ryan", name: "Ryan — Confident", locale: "en-US", gender: "male", speed: 1 },
  { id: "vo_zoe", name: "Zoe — Energetic", locale: "en-US", gender: "female", speed: 1.15 },
  { id: "vo_oliver", name: "Oliver — British", locale: "en-GB", gender: "male", speed: 1 },
  { id: "vo_ava", name: "Ava — Friendly", locale: "en-AU", gender: "female", speed: 1.05 },
];

/**
 * Chatterbox Multilingual V3 ships one built-in speaker condition pack and
 * supports these 23 language IDs. These are language presets for that honest,
 * single included speaker; additional speaker identities come from consented
 * voice clones or separately installed condition packs.
 */
export const CHATTERBOX_PRESET_VOICES: Voice[] = [
  { id: "vo_chatterbox_default", name: "Natural — English", locale: "en", gender: "female", speed: 1 },
  { id: "vo_chatterbox_zh", name: "Natural — 中文", locale: "zh", gender: "female", speed: 1 },
  { id: "vo_chatterbox_ja", name: "Natural — 日本語", locale: "ja", gender: "female", speed: 1 },
  { id: "vo_chatterbox_ko", name: "Natural — 한국어", locale: "ko", gender: "female", speed: 1 },
  { id: "vo_chatterbox_es", name: "Natural — Español", locale: "es", gender: "female", speed: 1 },
  { id: "vo_chatterbox_fr", name: "Natural — Français", locale: "fr", gender: "female", speed: 1 },
  { id: "vo_chatterbox_de", name: "Natural — Deutsch", locale: "de", gender: "female", speed: 1 },
  { id: "vo_chatterbox_it", name: "Natural — Italiano", locale: "it", gender: "female", speed: 1 },
  { id: "vo_chatterbox_pt", name: "Natural — Português", locale: "pt", gender: "female", speed: 1 },
  { id: "vo_chatterbox_ar", name: "Natural — العربية", locale: "ar", gender: "female", speed: 1 },
  { id: "vo_chatterbox_da", name: "Natural — Dansk", locale: "da", gender: "female", speed: 1 },
  { id: "vo_chatterbox_el", name: "Natural — Ελληνικά", locale: "el", gender: "female", speed: 1 },
  { id: "vo_chatterbox_fi", name: "Natural — Suomi", locale: "fi", gender: "female", speed: 1 },
  { id: "vo_chatterbox_he", name: "Natural — עברית", locale: "he", gender: "female", speed: 1 },
  { id: "vo_chatterbox_hi", name: "Natural — हिन्दी", locale: "hi", gender: "female", speed: 1 },
  { id: "vo_chatterbox_ms", name: "Natural — Bahasa Melayu", locale: "ms", gender: "female", speed: 1 },
  { id: "vo_chatterbox_nl", name: "Natural — Nederlands", locale: "nl", gender: "female", speed: 1 },
  { id: "vo_chatterbox_no", name: "Natural — Norsk", locale: "no", gender: "female", speed: 1 },
  { id: "vo_chatterbox_pl", name: "Natural — Polski", locale: "pl", gender: "female", speed: 1 },
  { id: "vo_chatterbox_ru", name: "Natural — Русский", locale: "ru", gender: "female", speed: 1 },
  { id: "vo_chatterbox_sv", name: "Natural — Svenska", locale: "sv", gender: "female", speed: 1 },
  { id: "vo_chatterbox_sw", name: "Natural — Kiswahili", locale: "sw", gender: "female", speed: 1 },
  { id: "vo_chatterbox_tr", name: "Natural — Türkçe", locale: "tr", gender: "female", speed: 1 },
];

export const CHATTERBOX_PRESET_VOICE_IDS = CHATTERBOX_PRESET_VOICES.map(
  (voice) => voice.id,
);

export const VOICES: Voice[] = [
  ...LEGACY_PROVIDER_VOICES,
  ...CHATTERBOX_PRESET_VOICES,
];

export const RATIO_CSS: Record<string, string> = {
  "9:16": "9 / 16",
  "1:1": "1 / 1",
  "16:9": "16 / 9",
  "4:5": "4 / 5",
};
